import { useEffect, useState } from 'react'
import { VolumeX } from 'lucide-react'
import { cn } from '../../lib/cn'
import {
  onSoundChange,
  restoreSoundPreference,
  setSoundEnabled,
  soundEnabled,
  soundReady,
  unlock,
} from '../../lib/stageAudio'

/**
 * The screen's sound switch — and the gesture that starts the audio engine.
 *
 * A browser will not make a sound until the page has been clicked or typed
 * into, and the projector laptop is opened, dragged to the second display and
 * then left alone. So this is both the control and the permission: whoever
 * sets the hall up presses it once, and the draw has sound for the rest of
 * the day.
 *
 * **Off by default**, deliberately. A screen that starts making noise the
 * moment it is opened is a screen somebody mutes at the operating system
 * level during setup — and then the draw is silent when it matters, with
 * nothing on the page to explain why.
 *
 * It is loud while off and **gone** while on: before the event it has to be
 * found, and afterwards nothing belongs in the corner of a projection — not a
 * faded pill, not one that reappears when a cursor crosses it. Once the sound
 * is on the button is unmounted.
 *
 * **M is the way back.** The component stays mounted even with nothing
 * rendered, so the key handler and the re-arm listener keep working, and the
 * off-state button carries the hint so whoever sets the hall up learns it
 * before it disappears.
 */
export function SoundToggle() {
  const [, bump] = useState(0)

  useEffect(() => {
    restoreSoundPreference()
    bump((n) => n + 1)
    return onSoundChange(() => bump((n) => n + 1))
  }, [])

  /*
   * Any click or key press anywhere re-arms the engine.
   *
   * A backgrounded tab gets its `AudioContext` suspended by the browser, and
   * the hall's laptop spends the ceremony behind the admin console. Without
   * this, sound worked until somebody switched windows and then quietly
   * stopped — the hardest kind of fault to diagnose from the stage.
   */
  useEffect(() => {
    const rearm = () => {
      if (soundEnabled() && !soundReady()) void unlock()
    }
    window.addEventListener('pointerdown', rearm)
    window.addEventListener('keydown', rearm)
    document.addEventListener('visibilitychange', rearm)
    return () => {
      window.removeEventListener('pointerdown', rearm)
      window.removeEventListener('keydown', rearm)
      document.removeEventListener('visibilitychange', rearm)
    }
  }, [])

  /*
   * **M** toggles the sound, and is the only way back once the button is gone.
   *
   * A key rather than something on screen, because the one requirement is
   * that nothing shows on the projection — and a keypress is also the gesture
   * a browser needs to start the audio, so M works for switching it *on* as
   * well as off.
   *
   * The screen has no inputs of its own, so there is nothing to type into and
   * no risk of this firing mid-sentence; the guard is only for the modifier
   * combinations a browser or the OS owns.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'm') return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      void setSoundEnabled(!soundEnabled())
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const on = soundEnabled() && soundReady()

  /*
   * Gone once the sound is on — not faded, not transparent, not rendered.
   *
   * It was `opacity-0` with a hover reveal, which still left it sitting over
   * the footer whenever a cursor passed the corner, and a projector that has
   * a mouse plugged into it has a cursor. Nothing belongs on a projection for
   * the rest of the day, so it is unmounted.
   *
   * The way back is **M**, below. The component itself stays mounted — it is
   * only the button that is gone — so the key handler and the re-arm listener
   * keep working.
   */
  if (on) return null

  return (
    <button
      type="button"
      onClick={() => void setSoundEnabled(true)}
      aria-label="Turn the draw sound on"
      title="Press M at any time to mute"
      className={cn(
        'fixed bottom-[2vmin] right-[2vmin] z-[60] flex items-center gap-[1vmin] rounded-full',
        'bg-[#F0AD37] px-[1.8vmin] py-[1vmin] text-[1.5vmin] font-bold text-[#17365D]',
        'shadow-[0_4px_18px_rgba(0,0,0,0.3)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
      )}
    >
      <VolumeX className="size-[2vmin]" strokeWidth={2.4} />
      Turn sound on
      <span className="ml-[0.6vmin] rounded-[0.5vmin] bg-[#17365D]/15 px-[0.8vmin] text-[1.2vmin]">
        M to mute
      </span>
    </button>
  )
}
